<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAuthStore } from '../../../../stores/auth';
import { useSocketStore } from '../../../../stores/socketStore';
import IconChevronDown from '../../../icons/IconChevronDown.vue';
import IconChevronUp from '../../../icons/IconChevronUp.vue';
import IconClose from '../../../icons/IconClose.vue';
import PgeButton from '../PgeButton.vue';

const emit = defineEmits(['enter-group', 'refresh-groups']);

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
});

const trimmed = ref(true);
const bubble = ref(null);
const authStore = useAuthStore();
const socketStore = useSocketStore();

onMounted(() => {
  scrollToBottom();
});

function toggleActive() {
  trimmed.value = !trimmed.value;
}

function scrollToBottom() {
  const parent = bubble.value?.parentElement;
  const notificationsContainer = parent?.querySelector('[ref="notifications"]');
  if (notificationsContainer) {
    notificationsContainer.scrollTop = notificationsContainer.scrollHeight;
  }
}

// Names are whatever a member typed: rendered as text, never as markup (XN-4828).
const authorName = computed(() => props.notification.authorData?.fullName ?? '');
const groupName = computed(() => props.notification.groupData?.name ?? '');
const isPrivate = computed(() => groupName.value === 'one-to-one');

function handleReply() {
  const parentDiv = bubble.value?.parentElement?.querySelector('[ref="notifications"]');
  const groupId = props.notification.groupData?._id;

  if (parentDiv && groupId) {
    parentDiv.querySelectorAll(`[data-group="${groupId}"]`).forEach((child) => {
      child.remove();
    });
  }

  emit('enter-group', props.notification.groupData);
}

function hideNotification() {
  const { group } = props.notification;

  socketStore.socket.emit('user_group_select', {
    domain: window.store.subdomain,
    group,
    user_id: authStore.userData.id,
  });
}

function groupJoin() {
  const payload = {
    ...props.notification,
    domain: window.store.subdomain,
  };

  socketStore.socket.emit('user_group_join', payload, (group) => {
    emit('enter-group', group);
  });

  socketStore.socket.emit('user_group_message', {
    domain: window.store.subdomain,
    group: props.notification.groupId,
    user_id: authStore.userData.id,
    message: 'joined the group chat',
  });

  bubble.value?.remove();
}

function groupDeclined() {
  socketStore.socket.emit('user_group_decline', {
    ...props.notification,
    domain: window.store.subdomain,
  });

  bubble.value?.remove();
  emit('refresh-groups');
}

function groupIgnore() {
  socketStore.socket.emit('user_group_ignore', {
    ...props.notification,
    domain: window.store.subdomain,
  });

  bubble.value?.remove();
  emit('refresh-groups');
}
</script>

<template>
  <div class="notification-bubble" ref="bubble" :data-notification="notification._id" :data-group="notification.group">
    <div class="notification-bubble__group">
      <div class="notification-bubble__header">
        <div v-if="notification.authorData?.avatar" class="notification-bubble__avatar">
          <img :src="notification.authorData.avatar" alt="" />
        </div>
        <div class="notification-bubble__info">
          <template v-if="notification.type === 'group'">
            <strong>{{ authorName }}</strong>
            <template v-if="isPrivate"> invited you to a private discussion</template>
            <template v-else> invited you to the <strong>{{ groupName }}</strong></template>
          </template>
          <template v-else-if="notification.groupData && !isPrivate">{{ authorName }} in <strong>{{ groupName }}</strong></template>
          <template v-else>{{ authorName }}</template>
        </div>
      </div>

      <icon-close v-if="notification.type === 'message'" class="notification-bubble__close" size="20"
        @click="hideNotification" />
    </div>

    <div v-if="notification.type === 'message'" class="notification-bubble__message">
      <div class="notification-bubble__text">
        <div @click="toggleActive">
          <icon-chevron-up v-if="trimmed" color="#4b444d" />
          <icon-chevron-down v-else color="#4b444d" />
        </div>
        <span :class="trimmed ? 'clamp-1' : 'clamp-3'">
          {{ notification.message }}
        </span>
      </div>
      <div class="notification-bubble__reply" v-if="!trimmed">
        <pge-button template="text-only" label="Reply" icon="Reply" button-size="small" @click="handleReply" />
      </div>
    </div>

    <div class="notification-bubble__actions" v-if="notification.type === 'group'">
      <pge-button label="Decline" template="outlined" button-size="small" @click="groupDeclined" />
      <pge-button label="Ignore" template="outlined" button-size="small" @click="groupIgnore" />
      <pge-button label="Accept" button-size="small" @click="groupJoin" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notification-bubble {
  overflow: hidden;
  background-color: rgba(94, 47, 120, 0.56);
  width: 310px;
  padding: 0 0 1px 8px;
  border-radius: 8px 12px 12px 8px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__avatar {
    min-width: 20px;
    max-width: 20px;
    min-height: 20px;
    max-height: 20px;
    overflow: hidden;
    border-radius: 50%;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__close {
    align-self: flex-start;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &__group {
    border-radius: 8px 0 0 0;
    background: rgba(255, 255, 255, 0.85);
    color: #4b444d;
    padding: 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.1px;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 6px;

    strong {
      font-weight: 600;
      color: #1e1a1f;
    }

    svg {
      cursor: pointer;
    }
  }

  &__message {
    border-radius: 0 0 8px 8px;
    background: rgba(255, 255, 255, 0.85);
    color: #4b444d;
    padding: 0 12px 12px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-around;
    column-gap: 4px;
    border-radius: 0 0 8px 8px;
    background: rgba(255, 255, 255, 0.85);
    padding: 13px 8px 14px;
  }

  &__text {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    margin-bottom: 10px;

    svg {
      min-width: 14px;
      height: 8px;
      margin: 6px 12px 0 0;
      cursor: pointer;
    }
  }

  &__reply {
    min-width: 100%;
    display: flex;
    align-content: center;
    justify-content: flex-end;
  }
}
</style>
